export interface GenerationRun {
  processId: string;
  status: "RUNNING" | "COMPLETED" | "FAILED";
  initiatorType: "MANUAL" | "AUTOMATIC";
  initiator: string;
  startedAt: string;
  endedAt: string | null;
  totalCount: number;
  month: string | null;
  year: string | null;
}

export type RunSeverity = "success" | "info" | "danger" | "warning";

export function runStatusSeverity(status: GenerationRun["status"]): RunSeverity {
  switch (status) {
    case "COMPLETED": return "success";
    case "RUNNING": return "info";
    case "FAILED": return "danger";
    default: return "warning";
  }
}

export function formatRunDuration(run: GenerationRun, now: number = Date.now()): string {
  const start = Date.parse(run.startedAt);
  if (Number.isNaN(start)) return "—";
  if (run.status === "RUNNING" || !run.endedAt) {
    const mins = Math.max(0, Math.round((now - start) / 60000));
    return `running ${mins}m`;
  }
  const end = Date.parse(run.endedAt);
  if (Number.isNaN(end)) return "—";
  const ms = Math.max(0, end - start);
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/** The active run = the one whose processId matches processing-status.run_started_at, else the newest RUNNING. */
export function deriveActiveRun(runs: GenerationRun[], runStartedAt: string | null): GenerationRun | null {
  if (!runs || runs.length === 0) return null;
  if (runStartedAt) {
    const match = runs.find((r) => r.processId === runStartedAt);
    if (match) return match;
  }
  const running = runs
    .filter((r) => r.status === "RUNNING")
    .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
  return running[0] ?? null;
}
